import React, { useState, useEffect, lazy, Suspense } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import { getAllIndustries } from "../../service/industry/IndustryServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

// Lazy load IndustryDialog to avoid circular dependency
const IndustryDialog = lazy(() => import("./IndustryDialog").then(module => ({ 
  default: module.IndustryDialog 
})));

interface IndustrySelectorProps {
  value: any;
  onChange: (e: any) => void;
  className?: string;
}

interface Industry {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  label: string;
  value: number;
}

export const IndustrySelector: React.FC<IndustrySelectorProps> = ({
  value,
  onChange,
  className = "",
}) => {
  const dispatch = useDispatch();
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);


  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const response = await getAllIndustries(dispatch, {
        Page: 1,
        Limit: 100,
        SortBy: "ASC",
        SortProperty: "industry_name",
      });

      if (response) {
        const formattedIndustries = response?.List?.map((industry: any) => ({
          id: industry.industry_id,
          name: industry.industry_name,
          description: industry.description,
          isActive: industry.is_active,
          label: industry.name,
          value: industry.industry_id,
        }));

        setIndustries(formattedIndustries);
      }
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  };

  const handleIndustryCreated = (newIndustry: any) => {
    // Add the new industry to the list
    setIndustries([...industries, newIndustry]);

    // Select the new industry in the dropdown
    onChange({
      value: newIndustry.value,
      originalEvent: { target: { name: "industry_id" } },
    });
  };

  return (
    <div className="flex items-center">
      <Dropdown
        value={value}
        options={industries}
        onChange={onChange}
        optionLabel="name"
        placeholder="Select an Industry"
        className={`w-full ${className}`}
      />
      <Button
        className="rounded-sm border-2 border-gold bg-gold text-white px-4 py-2 hover:bg-white hover:text-gold transition-colors duration-300 ml-2"
        onClick={() => setDialogVisible(true)}
        tooltip="Create New Industry"
        tooltipOptions={{ position: "top" }}
        type="button"
      >
        <FontAwesomeIcon icon={faPlus} />
      </Button>

      {dialogVisible && (
        <Suspense fallback={<div>Loading...</div>}>
          <IndustryDialog
            visible={dialogVisible}
            onHide={() => setDialogVisible(false)}
            onIndustryCreated={handleIndustryCreated}
          />
        </Suspense>
      )}
    </div>
  );
};